import { useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'
import config from 'constants/config'
import zIndex from 'styles/tools/z-index'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { goBack, push } from 'connected-react-router'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import CommonHeaderCheckout from './CommonHeaderCheckout'
import StepsHeaderCheckout from './StepsHeaderCheckout/StepsHeaderCheckout'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { getTourCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import LayoutContainer from 'components/Common/LayoutContainer'
import CheckoutSubheader from 'checkout/Components/Shared/Headers/CheckoutSubheader'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import SecurePaymentHeaderCheckout from './SecurePaymentHeaderCheckout'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { useFlightDateChangeBackNavigation } from 'hooks/Flights/useFlightDateChangeBackNavigation'
import { getIsCharterFlightCheckout } from 'checkout/selectors/view/flights'
import { useMaldivesCharterContact } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesCharterContact'
import { ContactProvider } from 'contexts/contactContext'
import { getSupportContact } from 'selectors/supportContactSelector'
import { selectCheckoutSteps } from 'checkout/selectors/checkoutStepsSelectors'
import { setFlightDateChangeStep } from 'actions/FlightActions'
import type { FlightChangeDatesPageStepDefinition } from './StepsHeaderCheckout/steps/flights'
import { FlightChangeDatesPageStep } from './StepsHeaderCheckout/steps/flights'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import { getFlightChangeDatesPageLocation } from 'checkout/lib/utils/flights/dateChangeUtils'

const StickyContainer = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundColor && css`
    background: ${config.header.backgroundColor};
    color: ${props => props.theme.palette.neutral.default.eight};
  `}

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundGradientColor && css`
    background: linear-gradient(90deg, ${config.header.backgroundColor}, ${config.header.backgroundGradientColor});
  `}
`

interface Props {
  isCruise: boolean
  hasTourCheckoutSteps: boolean
  checkoutMode?: App.CheckoutCartMode
  isCharterFlightCheckout: boolean
  currentRegionCode: string
}

function HeaderCheckout(props: Props) {
  const { isCruise, hasTourCheckoutSteps, checkoutMode, isCharterFlightCheckout, currentRegionCode } = props
  const dispatch = useAppDispatch()
  const steps = useAppSelector(selectCheckoutSteps)
  const currentPathname = useAppSelector(state => state.router.location.pathname)
  const { handleBack: handleFlightDateChangeBack } = useFlightDateChangeBackNavigation()

  const isFlightChangeDatesMode = checkoutMode === 'flight-change-dates'

  const handleGoBack = useCallback(() => {
    if (isFlightChangeDatesMode) {
      handleFlightDateChangeBack()
    } else {
      dispatch(goBack())
    }
  }, [dispatch, isFlightChangeDatesMode, handleFlightDateChangeBack])

  const handleStepClick = useCallback<StepClickHandler>((stepIndex) => {
    const targetStep = steps[stepIndex]
    if (!targetStep) return

    if (isFlightChangeDatesMode) {
      const targetStepKey = targetStep.key as FlightChangeDatesPageStepDefinition

      // Use URL detection to determine current page location
      const { isOnPurchasePage, isOnInsurancePage } = getFlightChangeDatesPageLocation(currentPathname)

      // Determine if target step is on the flight change page (internal navigation)
      const isTargetOnFlightChangePage =
        targetStepKey === FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE ||
        targetStepKey === FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE ||
        targetStepKey === FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS

      // Set the target step in Redux
      dispatch(setFlightDateChangeStep(targetStepKey))

      // Handle navigation based on current and target locations
      if (isOnPurchasePage || isOnInsurancePage) {
        // From purchase or insurance page, navigate directly to flight change page if target is there
        if (isTargetOnFlightChangePage) {
          // Extract region code from current pathname (e.g., /au/checkout/...)
          const pathParts = currentPathname.split('/')
          const regionCode = pathParts[1] || 'au'
          dispatch(push(`/${regionCode}/checkout/flight-change-select-flight-to-change`))
        } else if (targetStepKey === FlightChangeDatesPageStep.INSURANCE_SELECT_DATES && isOnPurchasePage) {
          // From purchase to insurance - go back once
          dispatch(goBack())
        }
      }
      // If on flight change page and target is also on flight change page, no navigation needed
      // The Redux state change will handle the internal step change
    }
  }, [steps, isFlightChangeDatesMode, dispatch, currentPathname])

  const headerRef = useRef<HTMLElement>(null)

  useHeaderStateObserver('checkout', headerRef)

  const hasSteps = isCruise || hasTourCheckoutSteps || checkoutMode === 'flight-change-dates'

  useAppHeaderObserver(headerRef, 'sticky')

  const isSecurePayment = useAppSelector(state => state.router.location.pathname.includes('/pay'))
  const globalContact = useAppSelector(getSupportContact)

  // Maldives charter priority contact
  const priorityContact = useMaldivesCharterContact(currentRegionCode, isCharterFlightCheckout)

  const maldivesCharterContact = useMemo(() => {
    if (!priorityContact) return undefined

    return {
      local: priorityContact,
      international: globalContact.international,
      default: 'local' as const,
      defaultContact: globalContact.defaultContact,
    } as App.GeoContacts
  }, [priorityContact, globalContact])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.checkout}>
      <ContactProvider contact={maldivesCharterContact}>
        <StickyContainer data-testid="checkout-header" ref={headerRef}>
          <LayoutContainer size="xl">
            {isSecurePayment && <SecurePaymentHeaderCheckout />}
            {!hasSteps && !isSecurePayment && <CommonHeaderCheckout onBack={handleGoBack} />}
            {hasSteps && !isSecurePayment && <StepsHeaderCheckout onBack={handleGoBack} onStepClick={isFlightChangeDatesMode ? handleStepClick : undefined} isCruise={isCruise} />}
          </LayoutContainer>
        </StickyContainer>
        <CheckoutSubheader />
      </ContactProvider>
    </AnalyticsPageContext.Provider>
  )
}

const mapStateToProps = (state: App.State): Props => ({
  isCruise: getCruiseItems(state).length > 0,
  hasTourCheckoutSteps: getTourCheckoutSteps(state).length > 1,
  checkoutMode: findPostPurchaseCheckout(state.checkout.cart.mode) || undefined,
  isCharterFlightCheckout: getIsCharterFlightCheckout(state),
  currentRegionCode: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(HeaderCheckout)
